/* eslint-disable no-param-reassign */
import _ from 'lodash';
import moment from 'moment';
import Grid from './grid';
import Tooltip from './tooltip';
import { getMasterLinksByTaskId } from '../../../helpers/ganttHelpers';
import rights from '../../../../../components/rights';
import constants from '../../../../../helpers/constants';
import routerHelper from '$$routerHelper';
import drag_icon from '../../../../../svg/drag_task_bar.svg';
import workloadCoreHelpers from '../helpers';
import globalToast from '$$vueCmp/toast';

let TimelineInstance = null;

class Timeline {
  constructor() {
    TimelineInstance = this;
    this.config = null;
    this.ganttInstance = null;
    this.draggedTask = null;
    this.droppedDate = null;

    this._debouncedResourceRowDragEnter = _.debounce((e, resource) => {
      if (!this.config.openedResources[resource.id]) {
        Grid.changeCssClassForGridRow(resource.id, null, ['drag_over']);
        this.config.openedResources[resource.id] = true;
        this.ganttInstance.callEvent('workloadRecalculate');
      }

      e.preventDefault();

      return false;
    }, 1000);

    this.onEndTaskCellsEdit = () => {
      if (!this.config.taskCellsEditMode.isActive) {
        return;
      }

      const taskData = this.config.taskCellsEditMode.taskData;
      const resourceData = this.config.taskCellsEditMode.resourceData;
      const deltaEstimation = Object.values(this.config.taskCellsEditMode.deltaEstimations)
        .reduce((sum, e) => {
          sum += e;

          return sum;
        }, 0);

      resourceData.value = Math.round((resourceData.value + deltaEstimation) * 100) / 100;
      resourceData.customEstimation = Object.values(this.config.taskCellsEditMode.customDays);
      taskData.estimation = Math.round((taskData.estimation + deltaEstimation) * 100) / 100;
      taskData.changedResources = [resourceData];

      this.ganttInstance.callEvent('onUpdateTasksCustomEstimations', [[taskData]]);
      this.config.taskCellsEditMode = {};
    };
    document.addEventListener('click', e => {
      if (!this.config) {
        return;
      }
      if (!e.target.closest('.gantt_resource_marker')) {
        this.onEndTaskCellsEdit();
      }
    });
  }

  init(instance, config) {
    this.ganttInstance = instance;
    this.config = config;
  }

  renderRow(rowData, timeline) {
    if (this.ganttInstance.config.durationData.mode === 'hour') {
      return;
    }

    const rowDomElement = document.createElement('div');
    const scale = timeline.getScale();

    rowDomElement.setAttribute('resource_id', rowData.id);

    this._setRowCssClass(rowDomElement, rowData);

    rowDomElement.style.cssText = `
      height: ${Grid.getWorkloadItemHeight(rowData.id)}px;
      position: absolute;
      width: 100%;
      top: ${Grid.getWorkloadItemTop(rowData.id)}px`;

    if (rowData.tasksLayout) {
      return this._prepareResourceTasksRow(rowData, rowDomElement);
    }

    return this._prepareResourceRow(rowData, rowDomElement, timeline, scale);
  }

  _prepareResourceRow(rowData, rowDomElement, timeline, scale) {
    rowDomElement.ondragenter = e => {
      if (!this.draggedTask) {
        return false;
      }

      Grid.changeCssClassForGridRow(rowData.id, 'drag_over', this.config.openedResources[rowData.id] ? ['drag_over'] : null);
      this._debouncedResourceRowDragEnter.cancel();
      this._debouncedResourceRowDragEnter(e, rowData);
    };

    rowDomElement.onmouseover = e => {
      Grid.changeCssClassForGridRow(rowData.id, 'hover');
    };

    rowDomElement.onmouseleave = e => {
      Grid.changeCssClassForGridRow(rowData.id, null, ['hover', 'drag_over']);
    };

    rowDomElement.addEventListener('click', _.debounce(event => {
      let scrollXratio = event.offsetX / timeline.$task.offsetWidth;

      if (this.ganttInstance.getScrollState().width) {
        scrollXratio = event.offsetX / this.ganttInstance.getScrollState().width;
      }

      this.ganttInstance.callEvent('onClickWorkloadResourceRow', [rowData.id, scrollXratio]);
    }, 50));

    const scrollStateX = this.ganttInstance.getScrollState().x;

    for (let i = 0; i < scale.count; i++) {
      const delta = scale.left[i] - scrollStateX;

      if (delta < -timeline.$task.offsetWidth || delta > (2 * timeline.$task.offsetWidth)) {
        continue;
      }

      const preparedCellData = rowData.cellData[i];

      let el = null;

      if (!preparedCellData) {
        continue;
      }

      preparedCellData.sizes = {
        left: scale.left[i],
        width: scale.width[i],
        top: 0,
      };

      el = this._prepareResourceCell(rowData, preparedCellData);

      el && rowDomElement.appendChild(el);
    }

    return rowDomElement;
  }

  _prepareResourceTasksRow(resource, row) {
    const scale = this.ganttInstance.$ui.getView('workloadTimeline').getScale();
    const timelineContainer = this.ganttInstance.$ui.getView('workloadTimeline').$task_data;
    let isDblClicked = false;
    let visibleCount = 0;

    row.ondragenter = e => {
      this._debouncedResourceRowDragEnter.cancel();
      Grid.changeCssClassForGridRow(resource.id, null, ['drag_over']);

      if (e.target && e.target.closest('.gantt_resource_row')) {
        const dropMirror = document.getElementById('dropMirror');

        if (dropMirror) {
          row.appendChild(dropMirror);
        }
      }
      e.preventDefault();

      return false;
    };

    row.ondragover = this._onDragOverResourceTasksLayout;

    row.ondragleave = function (e) {
      if (e.toElement && e.toElement.closest('.gantt_resource_row')) {
        const dropMirror = document.getElementById('dropMirror');

        if (dropMirror) {
          dropMirror.style.display = 'none';
        }
      }

      return false;
    };

    row.ondrop = this._onDropResourceTasksLayout;

    resource.tasksByGanttId.forEach(task => {
      if (!this.ganttInstance.isTaskExists(task.taskData.id) || task._onlyForWorkloadCalculation) { //
        return;
      }

      if (!this._checkVisibilityForTaskBar(task, resource)) {
        return;
      }

      const index = resource.tasksLayout.taskIndexes[task.taskData.id] || 0;

      if (resource.isPartial && index >= this.config.countOfUnassignedTaskRows) {
        return;
      }
      visibleCount++;

      const bar = this._renderTaskBar(task.taskData.id);

      if (!bar) {
        return;
      }

      bar.style.top = `${index * this.config.ROW_HEIGHT + this.config.TASK_BAR_TOP_POSITION}px`;

      if (rights.project.hasRight(task.gantt_id, 'static_fields') && !this.ganttInstance.ganttDataApi.isExport()) {
        bar.setAttribute('draggable', true);
      }

      bar.onclick = _.debounce(e => {
        if (isDblClicked || resource.unassigned) {
          isDblClicked = false;

          return;
        }

        if (this.config.viewMode !== 'hours' || task.taskData.type === this.ganttInstance.config.types.milestone) {
          return;
        }

        const isSelected = this._checkIsSelectedTaskBar(task.taskData, resource.resource_id);

        this.config.selectedTask = {
          id: isSelected ? null : task.taskData.id,
          resourceId: resource.resource_id,
        };

        this.ganttInstance.callEvent('onClickWorkloadTaskBar', [resource.id]);

        Tooltip.hideTooltip();
      }, 200);

      bar.addEventListener('dblclick', e => {
        if (this.ganttInstance.ganttDataApi.isExport()) {
          return;
        }

        isDblClicked = true;

        const currentRoute = routerHelper.getCurrentRoute();
        let newRoutePathArray;

        if (currentRoute.name.includes('workloadTaskRoute')) {
          newRoutePathArray = currentRoute.path.split('/').slice(0, 2);
        } else {
          newRoutePathArray = currentRoute.path.split('/').slice(0, 4);
        }

        newRoutePathArray.push('task', task.taskData.id);
        routerHelper.changeRoute(newRoutePathArray.join('/'));

        userExtAnalytics.log('gantt_task_settings_open', {
          from: routerHelper.analyticsFromRoute(),
          route: `${routerHelper.analyticsRoute()}/DoubleClick ${routerHelper.analyticsRoute().includes('gantt') ? 'Workload' : 'Task'}`,
        });
      });

      const debouncedMouseEnter = _.debounce(e => {
        Tooltip.showTaskBarTooltip(task.taskData, bar, e);
      }, 300);

      bar.onmouseenter = debouncedMouseEnter;

      bar.onmouseleave = function (e) {
        debouncedMouseEnter.cancel();
        Tooltip.hideTooltip();
      };

      bar.ondragstart = e => {
        const dropMirror = this._createDropMirror(task.taskData.id);

        Tooltip.hideTooltip();

        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('taskId', task.taskData.id);
        e.dataTransfer.setData('resourceId', resource.id);
        e.target.classList.add('dragged');
        timelineContainer.classList.add('on_drag');
        this.draggedTask = this.ganttInstance.getTask(task.taskData.id);// needs prepared task data
        this.draggedTask.dragOffset = e.offsetX;

        row.appendChild(dropMirror);

        userExtAnalytics.log('workload_task_drag_start', {
          from: this._getAnalyticType(),
        });

        return true;
      };

      bar.ondragend = e => {
        const dropMirror = document.getElementById('dropMirror');

        e.target.classList.remove('dragged');
        timelineContainer.classList.remove('on_drag');
        this.draggedTask = null;
        dropMirror && dropMirror.remove();

        return true;
      };

      row.appendChild(bar);

      if (this._checkIsSelectedTaskBar(task.taskData, resource.resource_id) && this.config.viewMode === 'hours') {
        const topIndex = (resource.tasksLayout.taskIndexes[task.taskData.id] + 1) || 0;
        const openedTaskBar = this._prepareOpenedTaskBar(task.taskData, resource, scale, topIndex);

        row.appendChild(openedTaskBar);
      }
    });

    if (resource.isPartial && resource.tasksByGanttId.length > visibleCount) {
      row.appendChild(this._createMoreButtonForResource(resource.tasksByGanttId.length, visibleCount));
    }

    return row;
  }

  _prepareResourceCell(resource, preparedCellData) {
    const {
      sizes, totalTasksLength, resourceDayInfo, totalGanttIds, currentTracexValue,
    } = preparedCellData;
    const totalHours = resourceDayInfo.totalHours;
    const workHours = resourceDayInfo.workHours;
    const cssPostfix = this._getCSSByWorkHours(totalHours, workHours, totalTasksLength);

    if (workHours === 0 && !resourceDayInfo.isWeekend && !this.ganttInstance.ganttDataApi.isExport()) {
      return;
    }

    let css = `gantt_resource_marker gantt_resource_marker_${cssPostfix}`;
    const el = document.createElement('div');
    const loadNumber = (totalHours / workHours) * 100;
    const loadInPercents = Math.floor(loadNumber < 1 ? 1 : loadNumber);

    el.style.cssText = `left: ${sizes.left}px;
                        width: ${sizes.width}px;
                        position: absolute;
                        height: ${resource.unassigned ? this.config.ROW_HEIGHT - 2 : this.config.ROW_HEIGHT}px;
                        line-height: ${this.config.ROW_HEIGHT}px;
                        top: ${sizes.top}px;`;

    if (resource.unassigned) {
      css += ' unassigned';

      if (totalTasksLength) {
        css += ' not_empty';
      }

      el.className = css;

      return el;
    }

    if (!resourceDayInfo.isWeekend || totalHours) {
      if (this.config.viewMode === 'hours' || this.config.viewMode === 'percents') {
        el.innerHTML = `${this.config.viewMode === 'hours' ? workloadCoreHelpers.formatNumber(totalHours) : (loadInPercents === Infinity ? 100 : (loadInPercents || ''))}`;
      } else {
        el.innerHTML = totalTasksLength;
      }

      if (workHours) {
        const loadLayer = document.createElement('div');

        loadLayer.className = 'load_layer';
        loadLayer.style.cssText = `height: ${loadInPercents > 100 ? 100 : loadInPercents}%`;
        el.appendChild(loadLayer);
      }
    }

    el.setAttribute('trace_x', currentTracexValue);

    const debouncedShowResourceCellTooltip = _.debounce(e => {
      Tooltip.showResourceCellTooltip(e, totalTasksLength, totalHours, workHours, totalGanttIds);
    }, 300);

    el.addEventListener('mouseover', debouncedShowResourceCellTooltip);

    el.addEventListener('mouseleave', () => {
      debouncedShowResourceCellTooltip.cancel();
      Tooltip.hideTooltip();
    });

    if (resourceDayInfo.isWeekend) {
      css += ' weekend';
    }

    el.className = css;

    return el;
  }

  _renderTaskBar(task, isMirror) {
    let taskBar = this.ganttInstance.$ui.layers.taskBar();
    const taskData = _.isObject(task) ? this.ganttInstance.__scaleTask(task) : this.ganttInstance.__scaleTask(this.ganttInstance.getTask(task));
    const dragArea = document.createElement('div');

    dragArea.innerHTML = `<div style="background-color: ${`${constants.COLORS[taskData.color]}dd`}">${drag_icon}</div>`;
    dragArea.classList.add('drag_area');

    taskData.workloadMode = true;

    taskBar = taskBar.render(taskData, this.ganttInstance.$ui.getView('workloadTimeline'));

    if (!taskBar) {
      return;
    }

    taskBar.style.height = `${this.config.RESOURCE_TASK_HEIGHT}px`;
    taskBar.style.lineHeight = `${this.config.RESOURCE_TASK_HEIGHT}px`;

    if (rights.project.hasRight(taskData.gantt_id, 'static_fields')) {
      taskBar.appendChild(dragArea);
    }

    const start = this.ganttInstance.config.start_date;
    const end = this.ganttInstance.config.end_date;

    if (start && end && !isMirror) {
      const leftCondition = taskData.start_date.valueOf() < start.valueOf() && taskData.end_date.valueOf() > start.valueOf();
      const rightCondition = taskData.start_date.valueOf() < end.valueOf() && taskData.end_date.valueOf() > end.valueOf();

      leftCondition && this._renderTaskBarCutArea(taskBar, 'left', taskData.color);
      rightCondition && this._renderTaskBarCutArea(taskBar, 'right', taskData.color);
    }

    return taskBar;
  }

  _prepareOpenedTaskBar(task, resource, scale, topIndex) {
    const bar = document.createElement('div');
    const assignedResourcesOnTask = this.ganttInstance.ganttDataApi.getAssignedResourcesOnTask(task.gantt_id, task.id);
    const resourceTask = assignedResourcesOnTask.find(res => res.resource_id === resource.resource_id);
    const resourceTaskData = {
      task: { ...task },
      resource_task_id: resourceTask.id,
      resource_id: resourceTask.resource_id,
      value: resourceTask.value,
    };

    resource.selectedTaskTimeTable.forEach((obj, index) => {
      const currentTraceXValue = obj.start_date.valueOf();
      const dayPart = index + 1;
      const traceIndex = scale.trace_indexes[currentTraceXValue];

      const sizes = {
        left: scale.left[traceIndex],
        top: topIndex * this.config.ROW_HEIGHT,
        width: scale.width[traceIndex],
      };

      const cell = this._renderTaskCell(resourceTaskData, sizes, obj.hours, currentTraceXValue, dayPart, obj.isCustom);

      cell && bar.appendChild(cell);
    });

    return bar;
  }

  _renderTaskCell(data, sizes, totalHours, currentTraceXValue, dayPart, isCustom) {
    const timeline = this.ganttInstance.$ui.getView('workloadTimeline');

    if (timeline.getScale().trace_indexes[currentTraceXValue] === undefined) {
      return;
    }

    const el = document.createElement('input');
    const css = `gantt_resource_marker gantt_resource_marker_task${isCustom ? ' custom' : ''}`;

    const cellValue = workloadCoreHelpers.formatNumber(totalHours);

    el.setAttribute('type', 'text');
    el.setAttribute('value', cellValue);
    el.setAttribute('old_estimation', totalHours);

    el.addEventListener('focus', function () {
      this.select();
      // _state.editTaskCellMode = true;
      // _afterTaskCellEditDebounced.cancel();
    });

    // const hasRightWorkload = ((gantt.config.order && gantt.config.order.some(id => rights.project.hasRight(id, 'static_fields')))
    //   || rights.project.hasRight(gantt.config.gantt_id, 'static_fields'));

    if (this.ganttInstance.ganttDataApi.isExport() || !rights.project.hasRight(data.task.gantt_id, 'static_fields')) {
      el.setAttribute('readonly', '');
    }

    el.addEventListener('blur', () => {
      const old_estimation = el.getAttribute('old_estimation');
      const newValue = parseFloat((`${el.value}`).replace(',', '.').replace('-', '')) || 0;

      el.value = newValue;

      // _afterTaskCellEditDebounced();

      if (newValue === +old_estimation) {
        return;
      }

      const delta_estimation = Math.round((newValue - +old_estimation) * 100) / 100;

      this.config.taskCellsEditMode.isActive = true;

      if (!this.config.taskCellsEditMode.taskData) {
        this.config.taskCellsEditMode.taskData = data.task;
      }

      if (!this.config.taskCellsEditMode.customDays) {
        this.config.taskCellsEditMode.customDays = {};
      }

      this.config.taskCellsEditMode.customDays[dayPart] = {
        resource_task_id: data.resource_task_id,
        day_part: dayPart,
        day_time: currentTraceXValue,
        estimation: newValue,
        action: 'insert',
      };

      if (!this.config.taskCellsEditMode.deltaEstimations) {
        this.config.taskCellsEditMode.deltaEstimations = {};
      }

      this.config.taskCellsEditMode.deltaEstimations[dayPart] = delta_estimation;

      if (!this.config.taskCellsEditMode.resourceData) {
        this.config.taskCellsEditMode.resourceData = {
          resource_id: data.resource_id,
          action: 'update',
          value: data.value,
        };
      }
    });

    el.addEventListener('keyup', e => {
      if (e.key === 'Enter' || e.key === 'Escape') {
        el.blur();
        this.onEndTaskCellsEdit();
      }
    });

    el.addEventListener('mouseover', _.debounce(Tooltip.hideTooltip, 280));
    el.addEventListener('mouseleave', _.debounce(Tooltip.hideTooltip, 280));

    el.className = css;

    el.style.cssText = [
      `left: ${sizes.left}px`,
      `width: ${sizes.width - 1}px`,
      'position: absolute',
      `height: ${this.config.ROW_HEIGHT - 1}px`,
      `line-height: ${this.config.ROW_HEIGHT - 1}px`,
      `top: ${sizes.top - this.config.TASK_BAR_TOP_POSITION}px`,
    ].join(';');

    return el;
  }

  _renderTaskBarCutArea(taskBar, sideClass, taskColor) {
    const cutArea = document.createElement('div');

    cutArea.innerHTML = `<div style="background-color: ${`${constants.COLORS[taskColor]}dd`}"></div>`;
    cutArea.classList.add('cut_area');
    cutArea.classList.add(sideClass);

    taskBar.appendChild(cutArea);
  }

  _createDropMirror(taskId) {
    const dropMirror = this._renderTaskBar(taskId, true);

    dropMirror.setAttribute('id', 'dropMirror');
    dropMirror.style.top = `${this.config.TASK_BAR_TOP_POSITION}px`;

    return dropMirror;
  }

  _onDragOverResourceTasksLayout(e) {
    if (!TimelineInstance.draggedTask) {
      return false;
    }

    const taskBarHovered = e.target.closest('.gantt_task_line');
    let offsetX = e.offsetX;

    Tooltip.hideTooltip();

    if (taskBarHovered) {
      offsetX += taskBarHovered.offsetLeft;
    }

    offsetX -= TimelineInstance.draggedTask.dragOffset;
    const scale = TimelineInstance.ganttInstance.$ui.getView('workloadTimeline').getScale();
    let index = ((offsetX - (offsetX % scale.col_width)) / scale.col_width);

    // need calculate index for skip_off_time == true
    if (TimelineInstance.ganttInstance.config.skip_off_time) {
      let i = 0;
      const left = (offsetX - (offsetX % scale.col_width));

      while (scale.left[i] <= left) {
        if (scale.width[i]) {
          index = i;
        }
        i++;
      }
    }

    const rowData = TimelineInstance.ganttInstance.getDatastore('workloadStore').getItem(this.getAttribute('resource_id'));
    let dropMirror = document.getElementById('dropMirror');

    if (!scale.trace_x[index]) {
      return false;
    }

    TimelineInstance.droppedDate = TimelineInstance.ganttInstance.getClosestWorkTime({
      date: scale.trace_x[index], dir: 'future', unit: TimelineInstance.ganttInstance.config.duration_unit, task: TimelineInstance.draggedTask,
    });

    const tempTaskObj = _.assignIn({}, TimelineInstance.draggedTask, {
      start_date: TimelineInstance.droppedDate,
      end_date: TimelineInstance.ganttInstance.calculateEndDate({
        start_date: scale.trace_x[index],
        duration: TimelineInstance.draggedTask.duration,
        task: TimelineInstance.draggedTask,
        unit: TimelineInstance.ganttInstance.config.duration_unit,
      }),
    });

    let isHoverAnotherTask = _.some(rowData.tasksByGanttId, o => {
      if (rowData.tasksLayout.taskIndexes[o.task_id] > 0) {
        return false;
      }

      const startCond = o.taskData.start_date.valueOf() < tempTaskObj.end_date.valueOf();
      const endCond = o.taskData.end_date.valueOf() > scale.trace_x[index].valueOf();

      return startCond && endCond;
    });

    const tempBar = TimelineInstance._renderTaskBar(tempTaskObj);

    if (!dropMirror) {
      dropMirror = TimelineInstance._createDropMirror(TimelineInstance.draggedTask.id);
      this.appendChild(dropMirror);
    }

    if (TimelineInstance.draggedTask.type === TimelineInstance.ganttInstance.config.types.milestone) {
      isHoverAnotherTask = false;
    }

    if (dropMirror) {
      dropMirror.style.left = tempBar.style.left;
      dropMirror.style.top = isHoverAnotherTask
        ? `${TimelineInstance.config.RESOURCE_TASK_HEIGHT + TimelineInstance.config.TASK_BAR_TOP_POSITION}px` : `${TimelineInstance.config.TASK_BAR_TOP_POSITION}px`;
      dropMirror.style.display = 'block';
      dropMirror.style.width = tempBar.style.width;
      dropMirror.classList[isHoverAnotherTask ? 'add' : 'remove']('under');
      dropMirror.style.height = isHoverAnotherTask ? `${TimelineInstance.config.RESOURCE_TASK_SMALL_HEIGHT}px` : `${TimelineInstance.config.RESOURCE_TASK_HEIGHT}px`;
    }

    e.preventDefault();

    return false;
  }

  _onDropResourceTasksLayout(e) {
    const taskId = +e.dataTransfer.getData('taskId');

    if (!taskId) {
      return;
    }

    const oldRowData = TimelineInstance.ganttInstance.getDatastore('workloadStore').getItem(e.dataTransfer.getData('resourceId'));
    const newRowData = TimelineInstance.ganttInstance.getDatastore('workloadStore').getItem(this.getAttribute('resource_id'));
    const task = TimelineInstance.ganttInstance.getTask(taskId);
    const changedResources = [];
    const resourceSettings = newRowData.resourceProjects?.find(settings => settings.projectId === task.gantt_id);
    const isBlockedStartDate = TimelineInstance._isTaskStartDateBlockedByLinkDependencies(task);
    const isChangedStartDate = task.start_date.valueOf() !== TimelineInstance.droppedDate.valueOf();

    Grid.changeCssClassForGridRow(this.getAttribute('resource_id'), null, ['drag_over']);

    const exceptionText = !newRowData.unassigned ? !resourceSettings
      ? 'workload_resource_not_exist_on_project' : resourceSettings.type !== constants.RESOURCE_TIME_TYPE
        ? 'workload_resource_not_valid_type_on_project' : '' : '';

    userExtAnalytics.log('workload_task_drop', {
      from: TimelineInstance._getAnalyticType(),
    });

    if (!newRowData.unassigned) {
      const messageData = {
        resourceName: newRowData.name,
        projectName: TimelineInstance.ganttInstance.ganttDataApi.getProjectItem(task.gantt_id).name,
      };

      if (!resourceSettings) {
        globalToast.info(__('workload_resource_not_exist_on_project', messageData), { timeout: 4000 });

        return false;
      }

      if (resourceSettings.type !== constants.RESOURCE_TIME_TYPE) {
        globalToast.info(__('workload_resource_not_valid_type_on_project', messageData), { timeout: 4000 });

        return false;
      }
    }

    TimelineInstance.ganttInstance.callEvent('onDropWorkloadTaskBar', [this.getAttribute('resource_id')]);

    if (oldRowData.resource_id !== newRowData.resource_id) {
      const isTaskAssigned = _.find(task.resources, res => res.resource_id === newRowData.resource_id);

      if (!oldRowData.unassigned) {
        changedResources.push({
          resource_id: oldRowData.resource_id,
          action: 'delete',
        });
      }

      let value = task.estimation || task.duration / 60;

      if (!oldRowData.unassigned) {
        const assignedResourcesOnTask = TimelineInstance.ganttInstance.ganttDataApi.getAssignedResourcesOnTask(task.gantt_id, task.id);

        value = assignedResourcesOnTask.find(res => res.resource_id === oldRowData.resource_id).value;
      }

      if (!isTaskAssigned && !newRowData.unassigned) {
        changedResources.push({
          resource_id: newRowData.resource_id,
          action: 'insert',
          value,
        });
      }
    }

    if ((isChangedStartDate && !isBlockedStartDate) || changedResources.length) {
      // TimelineInstance.ganttInstance.callEvent('onBeforeWorkloadTaskUpdate');

      task.start_date = TimelineInstance.droppedDate;
      task.end_date = TimelineInstance.ganttInstance.calculateEndDate({
        start_date: TimelineInstance.droppedDate,
        duration: task.duration,
        task,
        unit: TimelineInstance.ganttInstance.config.duration_unit,
      });

      task.start_date_string = moment(task.start_date).format('YYYY-MM-DD HH:mm:ss');
      task.end_date_string = moment(task.end_date).format('YYYY-MM-DD HH:mm:ss');

      // if (!task.auto_scheduling) {
      //   delete task.auto_scheduling;
      // }

      if (changedResources.length) {
        task.changedResources = changedResources;
      }

      TimelineInstance.ganttInstance.callEvent('workloadClearCache', [{ resourceIds: [newRowData.resource_id] }]);
      TimelineInstance.ganttInstance.updateTask(taskId, task);

      if (task.auto_scheduling) {
        TimelineInstance.ganttInstance.autoSchedule();
      }
    }

    if (isChangedStartDate && isBlockedStartDate) {
      globalToast.info(__('autoscheduling_block_start_date'), { timeout: 4000 });
    }

    // TimelineInstance.ganttInstance.isTaskExists(task.id) && TimelineInstance.ganttInstance.refreshTask(task.id);// just for refresh task row in grid

    e.stopPropagation();

    return false;
  }

  _checkVisibilityForTaskBar(task, resource) {
    const timeline = this.ganttInstance.$ui.getView('workloadTimeline');
    const scale = timeline.getScale();
    const startTimestamp = this.ganttInstance.date.day_start(new Date(task.taskData.start_date)).valueOf();
    const endTimestamp = this.ganttInstance.date.add(this.ganttInstance.date.day_start(new Date(task.taskData.end_date)), 1, 'day').valueOf();
    const startIndex = scale.trace_indexes[startTimestamp];
    const endIndex = scale.trace_indexes[endTimestamp];
    let isVisible = true;

    const startCheck = scale.left[startIndex] > (2 * timeline.$task.offsetWidth + this.ganttInstance.getScrollState().x);
    const endCheck = scale.left[endIndex] < (this.ganttInstance.getScrollState().x - timeline.$task.offsetWidth);

    if (startCheck || endCheck) {
      isVisible = false;
    }

    // TODO optimization
    if (!rights.project.hasRight(task.gantt_id, 'all_tasks') && resource.userId !== user.id) {
      isVisible = false;
    }

    return isVisible;
  }

  _checkIsSelectedTaskBar(task, resourceId) {
    const assignedResourcesOnTask = this.ganttInstance.ganttDataApi.getAssignedResourcesOnTask(task.gantt_id, task.id);
    const resourceTask = assignedResourcesOnTask.find(res => res.resource_id === resourceId);
    const isSelectedTask = task.id === this.config.selectedTask.id && resourceId === this.config.selectedTask.resourceId;

    if (!resourceTask) {
      if (isSelectedTask) {
        this.config.selectedTask = {}; // clear because unassigned task
      }

      return;
    }

    return isSelectedTask;
  }

  _createMoreButtonForResource(allTasksCount, visibleCount) {
    const addButton = document.createElement('div');

    addButton.className = 'show_more';
    addButton.style.height = `${this.config.ROW_HEIGHT}px`;
    addButton.innerHTML = `<span class="text">${visibleCount} ${__('key_of')} ${allTasksCount} ${__('show_more_unassigned')}</span>`;

    addButton.querySelector('.text').onclick = () => {
      this.config.countOfUnassignedTaskRows += this.config.PARTIAL_ROWS_COUNT;
      // _run({ fromCache: true }); TODO
      this.ganttInstance.callEvent('onClickWorkloadMoreButton');
    };

    return addButton;
  }

  _setRowCssClass(row, rowData) {
    row.className = 'gantt_resource_row';

    if (rowData.separator) {
      row.className += ' separator';
    }

    if (rowData.firstChild) {
      row.className += ' first';
    }

    if (rowData.lastChild) {
      row.className += ' last';
    }

    if (rowData.tasksLayout) {
      row.className += ' tasks';
    }

    if (rowData.opened) {
      row.className += ' open';
    }

    if (rowData.unassigned) {
      row.className += ' unassigned';
    }

    if (rowData.isPartial) {
      row.className += ' partial';
    }

    if (rowData.prevLastChild) {
      row.className += ' prev-last';
    }
  }

  _getCSSByWorkHours(hours, workHours, workTasks) {
    if (!workTasks) {
      return 'none';
    } if (hours === workHours) {
      return 'ok';
    } if (hours < workHours) {
      return 'underloaded';
    }

    return 'overloaded';
  }

  _getAnalyticType() {
    if (routerHelper.isGanttGridRoute()) {
      return 'gantt view';
    }

    if (routerHelper.isCommonWorkloadViewRoute()) {
      return 'general';
    }

    if (routerHelper.isWorkloadViewRoute()) {
      return 'workload tab ';
    }
  }

  _isTaskStartDateBlockedByLinkDependencies(taskData) {
    // TODO
    // if (!gantt.config.auto_scheduling && ganttViewModel.getActiveViewData().isSingleProject && !_state.isWorkloadPage) {
    //   return false;
    // }

    // if (!taskData.auto_scheduling && (!ganttViewModel.getActiveViewData().isSingleProject || _state.isWorkloadPage)) {
    //   return false;
    // }

    const children = TimelineInstance.ganttInstance.getChildren(taskData.parent);
    const predecessors = TimelineInstance.ganttInstance._getPredecessors(taskData, children.length > 1);

    if (predecessors.length === 1) {
      return true;
    }

    const mastersData = getMasterLinksByTaskId(TimelineInstance.ganttInstance, taskData.id);
    let shouldBlock = false;

    mastersData.forEach(masterData => {
      switch (masterData.linkType) {
      case TimelineInstance.ganttInstance.config.links.finish_to_start:
      case TimelineInstance.ganttInstance.config.links.start_to_start:
        shouldBlock = true;
        break;
      }
    });

    return shouldBlock;
  }
}

export default new Timeline();
